/**
 * Tag arithmetic and the tag-name cache, shared by the two note editors and the Cards bulk bar.
 *
 * The decidable parts live here rather than in the components because `web/` has no DOM in its test
 * environment — the convention throughout is a `.tsx` for the markup and a sibling `.ts` for the
 * rules, so the rules can be pinned. See `surfaces/read/notePanel.ts`, `surfaces/cards/query.ts`.
 */

import { listTags } from "./setup";

/**
 * One tag, cleaned up.
 *
 * **Anki separates tags with whitespace**, so a "tag" containing any is really two tags and would
 * be silently split the moment it reached the collection. Callers split first (`splitTags`); this
 * only trims. Returns `""` for anything that is not a tag, which callers drop.
 */
export function normalizeTag(raw: string): string {
  return raw.trim();
}

/**
 * Split a typed string into tags on any whitespace.
 *
 * `\s` rather than a literal space on purpose: this is a Japanese-language tool and an IME
 * routinely emits U+3000 IDEOGRAPHIC SPACE. Anki would treat that as part of the tag, so `勉強　N3`
 * would become one unusable tag instead of two.
 */
export function splitTags(input: string): string[] {
  return input.split(/\s+/).map(normalizeTag).filter(Boolean);
}

export interface TagEdit {
  add: string[];
  remove: string[];
}

/**
 * Parse the bulk `Tags…` prompt: space-separated tags, `-` prefixed to remove.
 *
 * A bare `-` is not a removal of the empty tag, it is a typo, and is dropped. Both lists are
 * de-duplicated; a tag given in both halves is kept in `remove` only, because asking to add and
 * remove the same tag in one breath most plausibly means the removal was the afterthought.
 */
export function parseTagSpec(input: string): TagEdit {
  const add: string[] = [];
  const remove: string[] = [];
  for (const token of splitTags(input)) {
    if (token.startsWith("-")) {
      const tag = normalizeTag(token.slice(1));
      if (tag) remove.push(tag);
    } else {
      add.push(token);
    }
  }
  const removed = new Set(remove.map(lower));
  return {
    add: unique(add).filter((t) => !removed.has(lower(t))),
    remove: unique(remove),
  };
}

/**
 * The tag list a note should end up with.
 *
 * **Case-insensitive throughout, which is Anki's own rule**: it treats `Leech` and `leech` as one
 * tag, so a comparison that did not would let the UI show two chips the collection will merge on
 * the next write. Existing tags keep whatever case they were stored with; a genuinely new tag keeps
 * the case it was typed with. Order is preserved — existing first, additions appended — so a save
 * does not reshuffle the chips under the cursor.
 */
export function applyTagEdit(current: readonly string[], edit: TagEdit): string[] {
  const removing = new Set(edit.remove.map(lower));
  const kept = unique(current.map(normalizeTag).filter(Boolean)).filter(
    (t) => !removing.has(lower(t)),
  );
  const held = new Set(kept.map(lower));
  const added = unique(edit.add.map(normalizeTag).filter(Boolean)).filter(
    (t) => !held.has(lower(t)),
  );
  return [...kept, ...added];
}

/** Whether `tag` is already on `tags`, by Anki's case-insensitive rule. */
export function hasTag(tags: readonly string[], tag: string): boolean {
  const wanted = lower(normalizeTag(tag));
  return !!wanted && tags.some((t) => lower(t) === wanted);
}

const lower = (t: string) => t.toLowerCase();
const unique = (tags: string[]) => {
  const seen = new Set<string>();
  return tags.filter((t) => {
    const key = lower(t);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// ---------------------------------------------------------------------------- suggestions

let pending: Promise<string[]> | null = null;

/**
 * Every tag name in use, for the `+ tag` combobox.
 *
 * **Fetched on first use and held for the session**, not on mount: Read's add panel opens on every
 * card you add, and a request per open would put `/api/setup/tags` — which counts notes per tag —
 * on that path for a list most opens never show. Going stale is the right trade: a tag added
 * elsewhere in this session is missing from the *suggestions* until reload, and typing it still
 * works, because this list has never been a permitted set.
 *
 * Machine tags are included deliberately. `Prio` exists to be applied by hand, so leaving it out of
 * the one place you would apply it would be exactly backwards.
 */
export function suggestedTags(): Promise<string[]> {
  pending ??= listTags()
    .then(({ tags }) => tags.map((t) => t.name))
    .catch(() => {
      // Suggestions are a convenience; free typing is the feature. Forget the failure so the next
      // open retries rather than caching an empty list for the session.
      pending = null;
      return [];
    });
  return pending;
}

/** Drop the cached list — after a write that could have created or removed a tag. */
export function forgetSuggestedTags(): void {
  pending = null;
}
